// Consommation et tentations : soldes, abonnements, paiements fractionnés.
import type { EventDef } from '../events';
import type { GameState, SubscriptionId } from '../../engine/state';
import { findSubscription, hasSubscription, streamingSubscription } from '../../game/subscriptions';
import { between, simpleEvent } from './kit';

/** Pendant les études, les prix suivent un budget d'étudiant. */
function student(state: GameState): boolean {
  return state.stage === 'prologue';
}

/** La plateforme de séries que vise une hausse de prix. */
function streamingId(state: GameState): SubscriptionId {
  return streamingSubscription(state)?.id ?? 'series';
}

/** Un logement à soi : en colocation ou en studio, pas chez les parents. */
function ownPlace(state: GameState): boolean {
  return state.housing !== 'parents';
}

export const EVENTS: EventDef[] = [
  // Les coups durs : la dépense est inévitable, seule la manière de payer change.
  simpleEvent({
    id: 'chaussures_prennent_eau',
    kind: 'imprevu',
    stages: ['prologue', 'stage1', 'stage2'],
    bad: true,
    shock: 5,
    maxCount: 1,
    season: [11, 12, 1, 2],
    params: (state) => ({
      sturdy: student(state) ? between(state, 100, 130) : between(state, 140, 180),
      cheap: between(state, 30, 45),
      resole: between(state, 40, 60),
    }),
    page: 'usure',
    choices: [
      { id: 'livret', effects: (_, p) => ({ livret: -p.sturdy }) },
      { id: 'premier_prix', effects: (_, p) => ({ cash: -p.cheap }) },
      { id: 'ressemeler', effects: (_, p) => ({ cash: -p.resole, energy: -2 }) },
    ],
  }),
  simpleEvent({
    id: 'ecouteur_perdu',
    kind: 'imprevu',
    stages: ['prologue', 'stage1', 'stage2'],
    bad: true,
    shock: 5,
    maxCount: 1,
    params: (state) => {
      const pair = student(state) ? between(state, 60, 90) : between(state, 90, 150);
      return { pair, single: Math.round(pair / 2), wired: between(state, 10, 20) };
    },
    choices: [
      { id: 'seul', effects: (_, p) => ({ cash: -p.single, energy: -1 }) },
      { id: 'paire', effects: (_, p) => ({ cash: -p.pair }) },
      { id: 'filaire', effects: (_, p) => ({ cash: -p.wired, wellbeing: -1 }) },
    ],
  }),
  // Pendant les études, l'impression est déjà traitée par le paquet « études » (le mémoire).
  simpleEvent({
    id: 'imprimante_cartouches',
    kind: 'imprevu',
    stages: ['stage1', 'stage2'],
    bad: true,
    shock: 5,
    maxCount: 1,
    params: (state) => {
      const official = between(state, 35, 55);
      return { official, printer: official + between(state, 10, 20), copies: between(state, 4, 8) };
    },
    choices: [
      { id: 'officielles', effects: (_, p) => ({ cash: -p.official }) },
      { id: 'neuve', effects: (_, p) => ({ cash: -p.printer }) },
      { id: 'papeterie', effects: (_, p) => ({ cash: -p.copies, energy: -2 }) },
    ],
  }),
  simpleEvent({
    id: 'matelas_creuse',
    kind: 'imprevu',
    stages: ['stage1', 'stage2'],
    bad: true,
    shock: 6,
    maxCount: 1,
    condition: ownPlace,
    params: (state) => ({ good: between(state, 350, 500), topper: between(state, 60, 90) }),
    page: 'fonds_urgence',
    choices: [
      { id: 'livret', effects: (_, p) => ({ livret: -p.good }) },
      { id: 'quatre_fois', effects: (_, p) => ({ loan: { amount: p.good, rate: 0.12, months: 4 } }) },
      { id: 'surmatelas', effects: (_, p) => ({ cash: -p.topper, fatigueDays: 30 }) },
    ],
  }),

  // Les bonnes surprises.
  simpleEvent({
    id: 'cagnotte_cashback',
    kind: 'chance',
    pleasureCategory: 'achats',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 1,
    params: (state) => {
      const gain = student(state) ? between(state, 20, 40) : between(state, 35, 70);
      return { gain, voucher: Math.round(gain * 1.1) };
    },
    choices: [
      { id: 'livret', effects: (_, p) => ({ livret: p.gain, wellbeing: 1 }) },
      { id: 'compte', effects: (_, p) => ({ cash: p.gain }) },
      { id: 'bon', effects: { pleasure: 3 } },
    ],
  }),
  simpleEvent({
    id: 'reprise_vieux_telephones',
    kind: 'chance',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 1,
    params: (state) => ({ gain: between(state, 40, 110) }),
    choices: [
      { id: 'compte', effects: (_, p) => ({ cash: p.gain, energy: -1 }) },
      { id: 'livret', effects: (_, p) => ({ livret: p.gain, energy: -1, wellbeing: 1 }) },
      { id: 'donner', effects: { wellbeing: 2 } },
    ],
  }),
  simpleEvent({
    id: 'baisse_prix_remboursee',
    kind: 'chance',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 1,
    params: (state) => ({ diff: student(state) ? between(state, 15, 30) : between(state, 25, 50) }),
    choices: [
      { id: 'reclamer', effects: (_, p) => ({ cash: p.diff, energy: -1 }) },
      { id: 'livret', effects: (_, p) => ({ livret: p.diff, energy: -1, wellbeing: 1 }) },
      { id: 'laisser' },
    ],
  }),
  simpleEvent({
    id: 'rappel_produit',
    kind: 'chance',
    pleasureCategory: 'achats',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 1,
    params: (state) => ({ amount: between(state, 25, 45) }),
    choices: [
      { id: 'compte', effects: (_, p) => ({ cash: p.amount, energy: -1 }) },
      { id: 'livret', effects: (_, p) => ({ livret: p.amount, energy: -1, wellbeing: 1 }) },
      { id: 'design', effects: { pleasure: 2, energy: -1 } },
    ],
  }),

  // Les tentations de saison.
  simpleEvent({
    id: 'soldes_hiver',
    kind: 'tentation',
    pleasureCategory: 'achats',
    stages: ['prologue', 'stage1', 'stage2'],
    season: [1],
    cooldownDays: 300,
    maxCount: 3,
    params: (state) => {
      const price = student(state) ? between(state, 60, 90) : between(state, 90, 140);
      return { price, second: Math.round(price * 0.7) };
    },
    choices: [
      { id: 'acheter', effects: (_, p) => ({ cash: -p.price, pleasure: 4 }) },
      { id: 'attendre', effects: { followUp: { event: 'soldes_deuxieme_demarque', days: 12 } } },
      { id: 'rien' },
    ],
  }),
  simpleEvent({
    id: 'soldes_deuxieme_demarque',
    kind: 'tentation',
    pleasureCategory: 'achats',
    stages: ['prologue', 'stage1', 'stage2'],
    manual: true,
    choices: [
      { id: 'prendre', effects: (_, p) => ({ cash: -p.second, pleasure: 2 }) },
      { id: 'laisser' },
    ],
  }),
  simpleEvent({
    id: 'soldes_ete',
    kind: 'tentation',
    pleasureCategory: 'achats',
    stages: ['prologue', 'stage1', 'stage2'],
    season: [7],
    cooldownDays: 300,
    maxCount: 3,
    params: (state) => ({
      total: student(state) ? between(state, 70, 100) : between(state, 110, 160),
      single: between(state, 20, 30),
    }),
    choices: [
      { id: 'tout', effects: (_, p) => ({ cash: -p.total, pleasure: 4 }) },
      { id: 'maillot', effects: (_, p) => ({ cash: -p.single, pleasure: 2 }) },
      { id: 'rien' },
    ],
  }),
  simpleEvent({
    id: 'marche_noel',
    kind: 'tentation',
    pleasureCategory: 'achats',
    stages: ['prologue', 'stage1', 'stage2'],
    season: [12],
    cooldownDays: 300,
    maxCount: 3,
    params: (state) => ({ pull: between(state, 30, 45), snack: between(state, 6, 9) }),
    choices: [
      { id: 'pull', effects: (_, p) => ({ cash: -p.pull, pleasure: 3 }) },
      { id: 'gaufre', effects: (_, p) => ({ cash: -p.snack, pleasure: 2, pleasureCategory: 'repas' }) },
      { id: 'flaner' },
    ],
  }),

  // S'abonner.
  simpleEvent({
    id: 'nouvelle_plateforme',
    kind: 'tentation',
    pleasureCategory: 'loisirs',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 2,
    condition: (state) => !hasSubscription(state, 'nouvelle_plateforme'),
    params: (state) => ({ monthly: between(state, 7, 12) }),
    choices: [
      {
        id: 'abonner',
        effects: (_, p) => ({ subscribe: { id: 'nouvelle_plateforme', amount: p.monthly }, pleasure: 3 }),
      },
      {
        id: 'remplacer',
        // L'ancienne plateforme laisse sa place à la nouvelle.
        visible: (state) => hasSubscription(state, 'series'),
        effects: (_, p) => ({
          unsubscribe: 'series',
          subscribe: { id: 'nouvelle_plateforme', amount: p.monthly },
          energy: -1,
          wellbeing: 1,
        }),
      },
      { id: 'attendre', effects: { followUp: { event: 'plateforme_saison_complete', days: 60 } } },
    ],
  }),
  simpleEvent({
    id: 'plateforme_saison_complete',
    kind: 'tentation',
    pleasureCategory: 'loisirs',
    stages: ['prologue', 'stage1', 'stage2'],
    manual: true,
    choices: [
      { id: 'mois', effects: (_, p) => ({ cash: -p.monthly, pleasure: 3, energy: -1 }) },
      { id: 'passer' },
    ],
  }),
  simpleEvent({
    id: 'salle_de_sport',
    kind: 'tentation',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 2,
    condition: (state) => !hasSubscription(state, 'salle_de_sport'),
    params: (state) => ({ monthly: student(state) ? between(state, 18, 25) : between(state, 28, 40) }),
    choices: [
      {
        id: 'inscrire',
        effects: (_, p) => ({
          subscribe: { id: 'salle_de_sport', amount: p.monthly },
          wellbeing: 2,
          followUp: { event: 'salle_de_sport_bilan', days: 90 },
        }),
      },
      { id: 'dehors', effects: { energy: -2, wellbeing: 1 } },
      { id: 'plus_tard' },
    ],
  }),
  simpleEvent({
    id: 'salle_de_sport_bilan',
    kind: 'quotidien',
    stages: ['prologue', 'stage1', 'stage2'],
    manual: true,
    choices: [
      { id: 'retourner', effects: { energy: -3, wellbeing: 3 } },
      {
        id: 'resilier',
        effects: { unsubscribe: 'salle_de_sport', energy: -1 },
      },
      { id: 'garder' },
    ],
  }),
  simpleEvent({
    id: 'box_mensuelle',
    kind: 'tentation',
    pleasureCategory: 'achats',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 1,
    params: (state) => ({ monthly: between(state, 15, 22), first: between(state, 5, 9) }),
    choices: [
      {
        id: 'abonner',
        effects: (_, p) => ({
          cash: -p.first,
          subscribe: { id: 'box_soins', amount: p.monthly },
          pleasure: 3,
        }),
      },
      { id: 'premier', effects: (_, p) => ({ cash: -p.first, pleasure: 2, energy: -1 }) },
      { id: 'non' },
    ],
  }),
  simpleEvent({
    id: 'jeu_mobile_pack',
    kind: 'tentation',
    pleasureCategory: 'loisirs',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 2,
    params: (state) => ({ pack: between(state, 8, 15), pass: between(state, 5, 10) }),
    choices: [
      { id: 'pack', effects: (_, p) => ({ cash: -p.pack, pleasure: 2 }) },
      {
        id: 'passe',
        visible: (state) => !hasSubscription(state, 'passe_jeu'),
        effects: (_, p) => ({ subscribe: { id: 'passe_jeu', amount: p.pass }, pleasure: 3 }),
      },
      { id: 'gratuit' },
    ],
  }),

  // Payer plus tard : fractionné, à crédit, avec la carte du magasin.
  simpleEvent({
    id: 'paiement_quatre_fois',
    kind: 'tentation',
    pleasureCategory: 'achats',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 2,
    params: (state) => ({ price: student(state) ? between(state, 300, 380) : between(state, 380, 480) }),
    choices: [
      {
        id: 'quatre_fois',
        effects: (_, p) => ({ loan: { amount: p.price, rate: 0.12, months: 4 }, pleasure: 4 }),
      },
      { id: 'comptant', effects: (_, p) => ({ cash: -p.price, pleasure: 4 }) },
      { id: 'fermer' },
    ],
  }),
  // Prendre la carte pose un repère : la « réserve d'argent » se proposera plus tard.
  simpleEvent({
    id: 'carte_magasin',
    kind: 'tentation',
    pleasureCategory: 'achats',
    stages: ['stage1', 'stage2'],
    maxCount: 1,
    params: (state) => {
      const price = between(state, 400, 550);
      const discount = Math.round(price / 10);
      return { price, discount, card: price - discount };
    },
    page: 'cout_credit',
    choices: [
      {
        id: 'carte',
        effects: (_, p) => ({
          loan: { amount: p.card, rate: 0.21, months: 12 },
          pleasure: 3,
          flag: 'carteMagasin',
        }),
      },
      {
        id: 'carte_comptant',
        effects: (_, p) => ({ cash: -p.card, pleasure: 3, energy: -1, flag: 'carteMagasin' }),
      },
      { id: 'comptant', effects: (_, p) => ({ cash: -p.price, pleasure: 3 }) },
      { id: 'reposer' },
    ],
  }),
  simpleEvent({
    id: 'reserve_carte_magasin',
    kind: 'tentation',
    pleasureCategory: 'achats',
    stages: ['stage1', 'stage2'],
    maxCount: 1,
    condition: (state) => state.flags.carteMagasin === true,
    params: (state) => ({ reserve: between(state, 1000, 2000), trip: between(state, 300, 450) }),
    choices: [
      {
        id: 'utiliser',
        effects: (_, p) => ({ loan: { amount: p.trip, rate: 0.21, months: 12 }, pleasure: 5 }),
      },
      { id: 'resilier', effects: { energy: -1 } },
      { id: 'ranger' },
    ],
  }),
  simpleEvent({
    id: 'smartphone_neuf',
    kind: 'tentation',
    pleasureCategory: 'achats',
    stages: ['stage1', 'stage2'],
    maxCount: 2,
    cooldownDays: 730,
    params: (state) => {
      const price = between(state, 1100, 1400);
      return { price, later: Math.round((price * 0.8) / 5) * 5 };
    },
    choices: [
      {
        id: 'credit',
        effects: (_, p) => ({
          loan: { amount: p.price, rate: 0.08, months: 24 },
          pleasure: 5,
          renew: { upgrade: 'telephone' },
        }),
      },
      { id: 'comptant', effects: (_, p) => ({ cash: -p.price, pleasure: 5, renew: { upgrade: 'telephone' } }) },
      { id: 'attendre', effects: { followUp: { event: 'smartphone_prix_baisse', days: 120 } } },
      { id: 'garder' },
    ],
  }),
  simpleEvent({
    id: 'smartphone_prix_baisse',
    kind: 'tentation',
    pleasureCategory: 'achats',
    stages: ['stage1', 'stage2'],
    manual: true,
    choices: [
      { id: 'acheter', effects: (_, p) => ({ cash: -p.later, pleasure: 4, renew: { upgrade: 'telephone' } }) },
      { id: 'garder' },
    ],
  }),

  // Acheter en ligne, sous l'influence des écrans.
  simpleEvent({
    id: 'panier_minuit',
    kind: 'tentation',
    pleasureCategory: 'achats',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 2,
    params: (state) => {
      const total = student(state) ? between(state, 60, 90) : between(state, 90, 140);
      return { total, half: Math.round(total * 0.4) };
    },
    page: 'loin_des_yeux',
    choices: [
      { id: 'valider', effects: (_, p) => ({ cash: -p.total, pleasure: 4 }) },
      { id: 'demain', effects: { followUp: { event: 'panier_au_reveil', days: 1 } } },
      { id: 'fermer' },
    ],
  }),
  simpleEvent({
    id: 'panier_au_reveil',
    kind: 'tentation',
    pleasureCategory: 'achats',
    stages: ['prologue', 'stage1', 'stage2'],
    manual: true,
    choices: [
      { id: 'partie', effects: (_, p) => ({ cash: -p.half, pleasure: 2 }) },
      { id: 'tout', effects: (_, p) => ({ cash: -p.total, pleasure: 3 }) },
      { id: 'vider' },
    ],
  }),
  simpleEvent({
    id: 'code_promo_createur',
    kind: 'tentation',
    pleasureCategory: 'achats',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 1,
    params: (state) => {
      const full = between(state, 80, 110);
      return { full, price: Math.round(full * 0.8), elsewhere: Math.round(full * 0.7) };
    },
    choices: [
      { id: 'commander', effects: (_, p) => ({ cash: -p.price, pleasure: 3 }) },
      {
        id: 'comparer',
        effects: { energy: -1, knowledge: 1, followUp: { event: 'sac_a_dos_ailleurs', days: 1 } },
      },
      { id: 'passer' },
    ],
  }),
  simpleEvent({
    id: 'sac_a_dos_ailleurs',
    kind: 'tentation',
    pleasureCategory: 'achats',
    stages: ['prologue', 'stage1', 'stage2'],
    manual: true,
    choices: [
      { id: 'acheter', effects: (_, p) => ({ cash: -p.elsewhere, pleasure: 3 }) },
      { id: 'laisser' },
    ],
  }),
  simpleEvent({
    id: 'billets_concert',
    kind: 'tentation',
    pleasureCategory: 'loisirs',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 2,
    params: (state) => {
      const face = between(state, 50, 75);
      return { face, premium: Math.round((face * 2.5) / 5) * 5 };
    },
    choices: [
      { id: 'premium', effects: (_, p) => ({ cash: -p.premium, pleasure: 5 }) },
      { id: 'bourse', effects: { energy: -2, followUp: { event: 'concert_place_liberee', days: 10 } } },
      { id: 'renoncer', effects: { wellbeing: -2 } },
    ],
  }),
  simpleEvent({
    id: 'concert_place_liberee',
    kind: 'tentation',
    pleasureCategory: 'loisirs',
    stages: ['prologue', 'stage1', 'stage2'],
    manual: true,
    choices: [
      { id: 'prendre', effects: (_, p) => ({ cash: -p.face, pleasure: 3 }) },
      { id: 'laisser', effects: { wellbeing: -1 } },
    ],
  }),

  // Les jeux d'argent : le résultat est écrit d'avance, comme la plupart du temps.
  simpleEvent({
    id: 'ticket_a_gratter',
    kind: 'tentation',
    pleasureCategory: 'loisirs',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 3,
    params: () => ({ price: 5, five: 25, jackpot: 100000 }),
    choices: [
      { id: 'un', effects: (_, p) => ({ cash: -p.price, pleasure: 1 }) },
      // Un ticket sur les cinq rend sa mise.
      { id: 'cinq', effects: (_, p) => ({ cash: p.price - p.five, pleasure: 2 }) },
      { id: 'passer' },
    ],
  }),
  simpleEvent({
    id: 'paris_sportifs',
    kind: 'tentation',
    pleasureCategory: 'loisirs',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 2,
    params: (state) => ({ stake: student(state) ? 10 : between(state, 20, 30) }),
    choices: [
      { id: 'parier', effects: (_, p) => ({ cash: -p.stake, pleasure: 2 }) },
      { id: 'regarder' },
    ],
  }),

  // Au quotidien : garanties et réparations.
  simpleEvent({
    id: 'extension_garantie',
    kind: 'quotidien',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 1,
    params: (state) => ({ cost: between(state, 25, 40) }),
    choices: [
      { id: 'prendre', effects: (_, p) => ({ cash: -p.cost, wellbeing: 1 }) },
      { id: 'refuser' },
    ],
  }),
  simpleEvent({
    id: 'seche_cheveux_garantie',
    kind: 'quotidien',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 1,
    params: (state) => ({ cost: between(state, 25, 45) }),
    choices: [
      { id: 'preuve', effects: { energy: -3 } },
      { id: 'racheter', effects: (_, p) => ({ cash: -p.cost }) },
      { id: 'air_libre', effects: { wellbeing: -1 } },
    ],
  }),
  simpleEvent({
    id: 'batterie_telephone',
    kind: 'quotidien',
    pleasureCategory: 'achats',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 1,
    params: (state) => ({
      battery: between(state, 50, 90),
      phone: student(state) ? between(state, 250, 350) : between(state, 350, 500),
    }),
    choices: [
      { id: 'batterie', effects: (_, p) => ({ cash: -p.battery, renew: { upgrade: 'telephone', yearsLeft: 1 } }) },
      { id: 'neuf', effects: (_, p) => ({ cash: -p.phone, pleasure: 3, renew: { upgrade: 'telephone' } }) },
      { id: 'recharger', effects: { wellbeing: -1 } },
    ],
  }),
  simpleEvent({
    id: 'jean_troue',
    kind: 'quotidien',
    pleasureCategory: 'achats',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 1,
    params: (state) => ({ repair: between(state, 12, 18), price: between(state, 50, 80) }),
    choices: [
      { id: 'reparer', effects: (_, p) => ({ cash: -p.repair }) },
      { id: 'neuf', effects: (_, p) => ({ cash: -p.price, pleasure: 2 }) },
      { id: 'tel_quel' },
    ],
  }),
  simpleEvent({
    id: 'tablette_reconditionnee',
    kind: 'quotidien',
    pleasureCategory: 'achats',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 1,
    params: (state) => {
      const price = between(state, 400, 500);
      return { price, refurb: Math.round((price * 0.7) / 5) * 5 };
    },
    choices: [
      { id: 'neuve', effects: (_, p) => ({ cash: -p.price, pleasure: 4 }) },
      { id: 'reconditionnee', effects: (_, p) => ({ cash: -p.refurb, pleasure: 3 }) },
      { id: 'rien' },
    ],
  }),
  simpleEvent({
    id: 'mode_petit_prix',
    kind: 'quotidien',
    pleasureCategory: 'achats',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 1,
    params: (state) => ({ cheap: between(state, 35, 50), durable: between(state, 70, 110) }),
    choices: [
      { id: 'tshirts', effects: (_, p) => ({ cash: -p.cheap, pleasure: 3 }) },
      { id: 'pull', effects: (_, p) => ({ cash: -p.durable, pleasure: 4 }) },
      { id: 'rien' },
    ],
  }),

  // Au quotidien : les petites dépenses qui reviennent. Le café du matin et la livraison de repas
  // sont réservés à la vie adulte : le paquet « études » a déjà leurs équivalents d'étudiant.
  simpleEvent({
    id: 'cafe_emporter',
    kind: 'quotidien',
    pleasureCategory: 'repas',
    stages: ['stage1', 'stage2'],
    maxCount: 1,
    params: () => ({ cup: 2, monthly: 40, friday: 8 }),
    page: 'budget',
    choices: [
      {
        id: 'rituel',
        effects: (_, p) => ({ addExpense: { id: 'courses', amount: p.monthly }, pleasure: 3 }),
      },
      {
        id: 'vendredi',
        effects: (_, p) => ({ addExpense: { id: 'courses', amount: p.friday }, pleasure: 1 }),
      },
      { id: 'thermos' },
    ],
  }),
  simpleEvent({
    id: 'trois_pour_deux',
    kind: 'quotidien',
    pleasureCategory: 'achats',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 2,
    params: (state) => {
      const one = between(state, 3, 4);
      return { one, three: one * 2 };
    },
    choices: [
      { id: 'trois', effects: (_, p) => ({ cash: -p.three, pleasure: 2 }) },
      { id: 'une', effects: (_, p) => ({ cash: -p.one, pleasure: 1 }) },
      { id: 'aucune' },
    ],
  }),
  simpleEvent({
    id: 'livraison_repas',
    kind: 'quotidien',
    pleasureCategory: 'repas',
    stages: ['stage1', 'stage2'],
    maxCount: 3,
    params: (state) => {
      const dish = between(state, 11, 14);
      return { dish, total: dish + between(state, 8, 11) };
    },
    choices: [
      { id: 'commander', effects: (_, p) => ({ cash: -p.total, pleasure: 2 }) },
      { id: 'emporter', effects: (_, p) => ({ cash: -p.dish, pleasure: 2, energy: -1 }) },
      { id: 'omelette' },
    ],
  }),
  simpleEvent({
    id: 'livraison_offerte',
    kind: 'quotidien',
    pleasureCategory: 'achats',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 2,
    params: (state) => ({ cart: between(state, 24, 30), shipping: between(state, 5, 7), threshold: 35 }),
    choices: [
      { id: 'livraison', effects: (_, p) => ({ cash: -(p.cart + p.shipping), pleasure: 1 }) },
      { id: 'completer', effects: (_, p) => ({ cash: -p.threshold, pleasure: 2 }) },
      { id: 'magasin', effects: (_, p) => ({ cash: -p.cart, pleasure: 1, energy: -1 }) },
      { id: 'abandonner' },
    ],
  }),
  simpleEvent({
    id: 'coupe_de_cheveux',
    kind: 'quotidien',
    pleasureCategory: 'soins',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 1,
    params: (state) => ({
      salon: student(state) ? between(state, 25, 35) : between(state, 35, 50),
      school: between(state, 8, 12),
    }),
    choices: [
      { id: 'salon', effects: (_, p) => ({ cash: -p.salon, pleasure: 2 }) },
      { id: 'ecole', effects: (_, p) => ({ cash: -p.school, pleasure: 1, energy: -1 }) },
      { id: 'toi_meme', effects: { wellbeing: -1 } },
    ],
  }),
  simpleEvent({
    id: 'roman_bibliotheque',
    kind: 'quotidien',
    pleasureCategory: 'loisirs',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 2,
    params: (state) => ({ price: between(state, 20, 24), used: between(state, 8, 12) }),
    choices: [
      { id: 'librairie', effects: (_, p) => ({ cash: -p.price, pleasure: 2 }) },
      { id: 'occasion', effects: (_, p) => ({ cash: -p.used, pleasure: 2, energy: -1 }) },
      { id: 'attente' },
    ],
  }),

  // Au quotidien : abonnements et offres d'essai.
  simpleEvent({
    id: 'cinema_illimite',
    kind: 'quotidien',
    pleasureCategory: 'loisirs',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 1,
    params: (state) =>
      student(state)
        ? { monthly: between(state, 17, 20), ticket: between(state, 6, 8) }
        : { monthly: between(state, 22, 26), ticket: between(state, 11, 13) },
    choices: [
      {
        id: 'carte',
        // La carte remplace les séances payées sur les envies : même dépense, plus de films.
        effects: (_, p) => ({
          subscribe: { id: 'cinema', amount: p.monthly },
          leisure: -p.monthly,
          perk: { id: 'cinema', wellbeing: 1, leisure: p.monthly },
          pleasure: 3,
          followUp: { event: 'cinema_illimite_fin', days: 365 },
        }),
      },
      { id: 'seance' },
    ],
  }),
  simpleEvent({
    id: 'cinema_illimite_fin',
    kind: 'quotidien',
    stages: ['prologue', 'stage1', 'stage2'],
    manual: true,
    // Le prix de la carte aujourd'hui, inflation comprise.
    params: (state) => ({ monthly: findSubscription(state, 'cinema')?.amount ?? state.perks.cinema?.leisure ?? 0 }),
    choices: [
      { id: 'renouveler', effects: { followUp: { event: 'cinema_illimite_fin', days: 365 } } },
      {
        id: 'resilier',
        effects: (state) => {
          const price = findSubscription(state, 'cinema')?.amount ?? state.perks.cinema?.leisure ?? 0;
          return { unsubscribe: 'cinema', leisure: price, endPerk: 'cinema' };
        },
      },
    ],
  }),
  simpleEvent({
    id: 'hausse_streaming',
    kind: 'quotidien',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 1,
    // Il faut une plateforme de séries sans publicités : la hausse vise la plus chère.
    condition: (state) => {
      const platform = streamingSubscription(state);
      return platform !== undefined && !platform.withAds;
    },
    params: (state) => {
      const plan = streamingSubscription(state)?.amount ?? 0;
      return { plan, hike: between(state, 2, 3), ads: Math.min(between(state, 5, 7), Math.round(plan / 2)) };
    },
    choices: [
      { id: 'accepter', effects: (state, p) => ({ changeSubscription: { id: streamingId(state), amount: p.hike } }) },
      {
        id: 'publicites',
        effects: (state, p) => ({
          changeSubscription: { id: streamingId(state), amount: -p.ads, withAds: true },
          wellbeing: -1,
        }),
      },
      { id: 'resilier', effects: (state) => ({ unsubscribe: streamingId(state), wellbeing: -2 }) },
    ],
  }),
  simpleEvent({
    id: 'essai_gratuit',
    kind: 'quotidien',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 1,
    params: (state) => ({ monthly: between(state, 10, 15) }),
    choices: [
      { id: 'essayer', effects: { wellbeing: 1, followUp: { event: 'essai_gratuit_fin', days: 28 } } },
      { id: 'refuser' },
    ],
  }),
  simpleEvent({
    id: 'essai_gratuit_fin',
    kind: 'quotidien',
    pleasureCategory: 'loisirs',
    stages: ['prologue', 'stage1', 'stage2'],
    manual: true,
    choices: [
      {
        id: 'garder',
        effects: (_, p) => ({ subscribe: { id: 'yoga', amount: p.monthly }, pleasure: 2 }),
      },
      { id: 'resilier', effects: { energy: -1 } },
    ],
  }),
  simpleEvent({
    id: 'remboursement_differe',
    kind: 'quotidien',
    pleasureCategory: 'achats',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 1,
    params: (state) => ({ price: between(state, 60, 90), refund: between(state, 20, 30) }),
    choices: [
      {
        id: 'dossier',
        effects: (_, p) => ({
          cash: -p.price,
          pleasure: 1,
          energy: -2,
          followUp: { event: 'remboursement_differe_virement', days: 45 },
        }),
      },
      { id: 'sans_dossier', effects: (_, p) => ({ cash: -p.price, pleasure: 1 }) },
      { id: 'rien' },
    ],
  }),
  simpleEvent({
    id: 'remboursement_differe_virement',
    kind: 'chance',
    stages: ['prologue', 'stage1', 'stage2'],
    manual: true,
    choices: [
      { id: 'livret', effects: (_, p) => ({ livret: p.refund, wellbeing: 1 }) },
      { id: 'compte', effects: (_, p) => ({ cash: p.refund }) },
    ],
  }),

  // Au quotidien : revendre, cliquer, être suivi par la publicité.
  simpleEvent({
    id: 'solde_appli_revente',
    kind: 'quotidien',
    pleasureCategory: 'achats',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 2,
    params: (state) => ({ gain: student(state) ? between(state, 40, 80) : between(state, 60, 120) }),
    choices: [
      { id: 'virer', effects: (_, p) => ({ cash: p.gain }) },
      { id: 'livret', effects: (_, p) => ({ livret: p.gain, wellbeing: 1 }) },
      { id: 'depenser', effects: { pleasure: 3 } },
    ],
  }),
  simpleEvent({
    id: 'pub_ciblee',
    kind: 'quotidien',
    pleasureCategory: 'achats',
    stages: ['prologue', 'stage1', 'stage2'],
    maxCount: 1,
    params: (state) => ({ price: between(state, 90, 130) }),
    choices: [
      { id: 'acheter', effects: (_, p) => ({ cash: -p.price, pleasure: 3 }) },
      { id: 'reglages', effects: { energy: -1, knowledge: 1 } },
      { id: 'ignorer' },
    ],
  }),
];
